import React, { useRef, useState } from 'react';

import {
  ActionIcon,
  Avatar,
  Box,
  Center,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  createStyles,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowsSort, IconSearch } from '@tabler/icons-react';
import useAthletes from 'hooks/athlete/useAthletes';
import type { AthleteList } from 'types';
import { getAvatarColor } from 'utils/colors';

export const PANEL_WIDTH = 280;
const ROSTER_PAGE_LIMIT = '100';
/** Distance from the bottom of the viewport at which the next page is requested. */
const SCROLL_PREFETCH_OFFSET = 200;

const useStyles = createStyles(theme => ({
  panel: {
    width: PANEL_WIDTH,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    backgroundColor: theme.white,
  },
  row: {
    display: 'block',
    width: '100%',
    padding: theme.spacing.xs,
    borderLeft: '3px solid transparent',
    '&:hover': { backgroundColor: theme.colors.gray[0] },
  },
  rowSelected: {
    borderLeftColor: theme.colors.kiloBlue[6],
    backgroundColor: theme.colors.kiloBlue[0],
  },
}));

interface AthletePanelProps {
  selectedAthleteId?: string;
  onSelectAthlete: (athlete: AthleteList) => void;
}

const initials = (athlete: AthleteList): string =>
  `${athlete.firstName?.[0] ?? ''}${athlete.lastName?.[0] ?? ''}`.toUpperCase();

const AthletePanel = ({ selectedAthleteId, onSelectAthlete }: AthletePanelProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 250);
  const [ascending, setAscending] = useState(true);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useAthletes({
    searchTerm: debouncedSearch || undefined,
    ordering: ascending ? 'first_name,last_name' : '-first_name,-last_name',
    limit: ROSTER_PAGE_LIMIT,
  });

  const athletes = data?.pages.flatMap(page => page.data.results ?? []) ?? [];
  const total = data?.pages[0]?.data.count ?? 0;

  const loadNextPageNearBottom = (): void => {
    const viewport = viewportRef.current;
    if (!viewport || !hasNextPage || isFetchingNextPage) return;

    const remaining = viewport.scrollHeight - (viewport.scrollTop + viewport.clientHeight);
    if (remaining < SCROLL_PREFETCH_OFFSET) fetchNextPage();
  };

  return (
    <Box className={classes.panel} data-cy='athletePanel'>
      <Stack spacing='xs' p='sm'>
        <Text size='sm' fw={600} color='dimmed' data-cy='athletePanelCount'>
          {total} total
        </Text>
        <Group spacing='xs' noWrap align='center'>
          <TextInput
            sx={{ flex: 1 }}
            size='sm'
            placeholder='Search athletes...'
            icon={<IconSearch size={14} />}
            value={searchTerm}
            onChange={event => setSearchTerm(event.currentTarget.value)}
            data-cy='athletePanelSearch'
          />
          <Tooltip label={ascending ? 'Sorted A→Z' : 'Sorted Z→A'} withArrow>
            <ActionIcon
              variant='default'
              size='lg'
              onClick={() => setAscending(current => !current)}
              aria-label={ascending ? 'Sort Z to A' : 'Sort A to Z'}
              data-cy='athletePanelSort'
            >
              <IconArrowsSort size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Stack>

      <ScrollArea
        sx={{ flex: 1, minHeight: 0 }}
        viewportRef={viewportRef}
        onScrollPositionChange={loadNextPageNearBottom}
      >
        {isError ? (
          <Text size='sm' p='sm' color='dimmed'>
            Something went wrong
          </Text>
        ) : isLoading ? (
          <Center mih={120}>
            <Loader size='sm' />
          </Center>
        ) : athletes.length === 0 ? (
          <Text size='sm' p='sm' color='dimmed' data-cy='athletePanelEmpty'>
            No athletes found
          </Text>
        ) : (
          <>
            {athletes.map(athlete => (
              <UnstyledButton
                key={athlete.athleteId}
                className={cx(classes.row, {
                  [classes.rowSelected]: athlete.athleteId === selectedAthleteId,
                })}
                onClick={() => onSelectAthlete(athlete)}
                data-cy='athletePanelRow'
              >
                <Group spacing='xs' noWrap>
                  <Avatar
                    size='sm'
                    radius='xl'
                    src={athlete.profilePicture}
                    color={getAvatarColor(athlete.athleteId)}
                  >
                    {initials(athlete)}
                  </Avatar>
                  <Text size='sm' lineClamp={1}>
                    {athlete.fullName}
                  </Text>
                </Group>
              </UnstyledButton>
            ))}
            {isFetchingNextPage && (
              <Center py='xs' data-cy='athletePanelNextPageLoader'>
                <Loader size='xs' />
              </Center>
            )}
          </>
        )}
      </ScrollArea>
    </Box>
  );
};

export default AthletePanel;
